import { useState } from 'react'
import { ArrowRight, CheckCircle2, RefreshCw, Server, Smartphone, XCircle } from 'lucide-react'
import { isHttpUrl, testServer } from '../lib/stremio'
import type { Settings } from '../types'
import { PairingPanel } from './PairingPanel'

interface Props {
  settings: Settings
  onSave: (value: Settings) => void
  onDone: () => void
}

type Check = { kind: 'idle' | 'loading' | 'ok' | 'error'; message?: string }

/**
 * Vela cannot play anything without a Stremio streaming server, and it ships
 * pointed at one particular address. Saying so once, up front, is far kinder
 * than letting someone browse happily and fail at the last step.
 */
export function SetupScreen({ settings, onSave, onDone }: Props) {
  const [serverUrl, setServerUrl] = useState(settings.serverUrl)
  const [check, setCheck] = useState<Check>({ kind: 'idle' })
  const [pairing, setPairing] = useState(false)

  async function verify() {
    if (!isHttpUrl(serverUrl)) { setCheck({ kind: 'error', message: 'Enter a valid HTTP or HTTPS address.' }); return }
    setCheck({ kind: 'loading' })
    try {
      const version = await testServer(serverUrl)
      onSave({ ...settings, serverUrl })
      setCheck({ kind: 'ok', message: `Connected — streaming server ${version}` })
    } catch (error) {
      setCheck({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Vela could not reach that address',
      })
    }
  }

  function finish() {
    if (!isHttpUrl(serverUrl)) {
      setCheck({ kind: 'error', message: 'Enter a valid HTTP or HTTPS address.' })
      return
    }
    onSave({ ...settings, serverUrl })
    onDone()
  }

  return (
    <div className="setup-screen">
      <div className="setup-panel">
        <span className="setup-mark"><Server size="2.25rem" /></span>
        <h1>Point Vela at your server</h1>
        <p>
          Vela finds titles on its own, but torrents are fetched and remuxed by a Stremio streaming
          server — not by the TV. Enter the address of yours to finish setting up.
        </p>

        <label>
          <span>Server address</span>
          <input
            value={serverUrl}
            onChange={(event) => setServerUrl(event.target.value)}
            data-focusable
            data-autofocus
            spellCheck={false}
            aria-label="Server address"
          />
        </label>

        {check.kind === 'ok' || check.kind === 'error' ? (
          <div className={`setup-result ${check.kind === 'ok' ? 'ok' : 'error'}`}>
            {check.kind === 'ok' ? <CheckCircle2 size="1.75rem" /> : <XCircle size="1.75rem" />}
            <span>{check.message}</span>
          </div>
        ) : null}

        <div className="setup-actions" data-nav-group="row" data-nav-key="setup">
          <button className="secondary-button" data-focusable onClick={() => setPairing(true)}>
            <Smartphone size="1.5rem" /> Set up with phone
          </button>
          <button className="secondary-button" data-focusable onClick={verify}>
            {check.kind === 'loading' ? <RefreshCw className="spin" size="1.5rem" /> : <Server size="1.5rem" />}
            Test connection
          </button>
          <button className="primary-button" data-focusable onClick={finish}>
            {check.kind === 'ok' ? 'Start watching' : 'Continue anyway'} <ArrowRight size="1.5rem" />
          </button>
        </div>

        <p className="setup-note">
          You can change this at any time in Settings. Without a reachable server Vela will still
          browse and search, but nothing will play.
        </p>
      </div>
      {pairing ? <PairingPanel values={{ serverUrl, torrentioUrl: settings.torrentioUrl }} onPair={(values) => { setServerUrl(values.serverUrl); onSave({ ...settings, ...values }); setCheck({ kind: 'ok', message: 'Addresses received from your phone' }) }} onClose={() => setPairing(false)} /> : null}
    </div>
  )
}
